import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { trackCallClick } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { MapPin, Phone, Mail } from "lucide-react";
import { useScrollAnimation, fadeInUp, fadeInLeft, fadeInRight, AnimatedDiv, AnimatedH2, AnimatedP, AnimatedH3 } from "@/hooks/useScrollAnimation";
import { useToast } from "@/hooks/use-toast";
import { useFormTracking } from "@/hooks/useFormTracking";
import PrivacyReassurance from "@/components/PrivacyReassurance";

const formSchema = z.object({
  firstName: z.string().trim().min(2, "First name must be at least 2 characters").max(50, "First name must be less than 50 characters"),
  lastName: z.string().trim().min(2, "Last name must be at least 2 characters").max(50, "Last name must be less than 50 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(20, "Phone number must be less than 20 characters"),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(1000, "Message must be less than 1000 characters"),
});

type FormValues = z.infer<typeof formSchema>;

const Contact = () => {
  const { ref, controls } = useScrollAnimation();
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const { trackStart, trackStep, trackComplete } = useFormTracking({
    formId: "contact",
    totalFields: 5,
  });

  const handleFieldBlur = (e: React.FocusEvent<HTMLFormElement>) => {
    const name = (e.target as unknown as HTMLInputElement | HTMLTextAreaElement)?.name;
    if (!name) return;
    const values = form.getValues();
    const filled = Object.values(values).filter((v) => String(v ?? "").trim().length > 0).length;
    trackStep(name, filled);
  };

  const onSubmit = async (data: FormValues) => {
    console.log("Message submitted:", data);
    
    try {
      await fetch("https://services.leadconnectorhq.com/hooks/WXXphothgoBK14OS4P4g/webhook-trigger/wA1AUrmq5w486VzRac6e", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          message: data.message,
          timestamp: new Date().toISOString(),
          formType: "Contact Form"
        }),
      });

      toast({
        title: "Message Sent!",
        description: "We'll get back to you as soon as possible.",
      });
      trackComplete();
      form.reset();
    } catch (error) {
      console.error("Error sending to webhook:", error);
      toast({
        title: "Message Sent!",
        description: "We'll get back to you as soon as possible.",
      });
      form.reset();
    }
  };
  return (
    <section className="py-12 md:py-20 bg-muted">
      <div className="container mx-auto px-4 md:px-6">
        <AnimatedDiv 
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={fadeInUp}
          className="text-center mb-10 md:mb-16"
        >
          <AnimatedH2 
            variants={fadeInUp}
            className="text-2xl sm:text-3xl md:text-5xl heading-luxury text-foreground mb-4 md:mb-6"
          >
            LET'S CONNECT
          </AnimatedH2>
          <AnimatedP 
            variants={fadeInUp}
            className="text-base md:text-lg text-luxury text-muted-foreground max-w-2xl mx-auto px-4"
          >
            Ready to find your dream home in St. George? Our local experts are here to guide you.
          </AnimatedP>
        </AnimatedDiv>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Info */}
          <AnimatedDiv 
            initial="hidden"
            animate={controls}
            variants={fadeInLeft}
            className="space-y-8"
          >
            <div>
              <AnimatedH3 
                variants={fadeInLeft}
                className="text-2xl heading-luxury text-foreground mb-6"
              >
                CONTACT INFORMATION
              </AnimatedH3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-foreground font-medium mb-1">Location</h4>
                    <p className="text-muted-foreground text-luxury">
                      St. George, Utah 84770<br />
                      Washington County
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-foreground font-medium mb-1">Phone</h4>
                    <a href="tel:+14357034422" onClick={() => trackCallClick("contact-block")} className="text-muted-foreground text-luxury hover:text-primary transition-colors">
                      (435) 703-4422
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-foreground font-medium mb-1">Email</h4>
                    <p className="text-muted-foreground text-luxury">
                      namgibbs@gmail.com
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <AnimatedH3 
                variants={fadeInLeft}
                className="text-xl heading-luxury text-foreground mb-4"
              >
                OFFICE HOURS
              </AnimatedH3>
              <div className="space-y-2 text-muted-foreground text-luxury">
                <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                <p>Saturday: 10:00 AM - 4:00 PM</p>
                <p>Sunday: By Appointment</p>
              </div>
            </div>
          </AnimatedDiv>

          {/* Contact Form */}
          <AnimatedDiv 
            initial="hidden"
            animate={controls}
            variants={fadeInRight}
          >
            <Card className="bg-white border-border shadow-lg">
              <CardContent className="p-8">
                <AnimatedH3 
                  variants={fadeInRight}
                  className="text-2xl heading-luxury text-foreground mb-6"
                >
                  SEND A MESSAGE
                </AnimatedH3>
              
                 <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    onFocusCapture={() => trackStart()}
                    onBlurCapture={handleFieldBlur}
                    className="space-y-4 md:space-y-6"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground font-semibold">First Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="John" 
                                {...field} 
                                className="border-border bg-white text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary h-11"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground font-semibold">Last Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Doe" 
                                {...field} 
                                className="border-border bg-white text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary h-11"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-semibold">Email</FormLabel>
                          <FormControl>
                            <Input 
                              type="email"
                              placeholder="john@example.com" 
                              {...field}
                              className="border-border bg-white text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary h-11"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-semibold">Phone Number</FormLabel>
                          <FormControl>
                            <Input 
                              type="tel"
                              placeholder="(435) 123-4567" 
                              {...field}
                              className="border-border bg-white text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary h-11"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-semibold">Message</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Tell us about your real estate needs in St. George..." 
                              {...field}
                              className="border-border bg-white text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary min-h-[120px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <PrivacyReassurance />

                    <Button 
                      type="submit" 
                      className="w-full bg-primary text-white hover:bg-primary/90 font-semibold"
                    >
                      Send Message
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </AnimatedDiv>
        </div>
      </div>
    </section>
  );
};

export default Contact;
